import { Fragment, Node, ResolvedPos, Schema } from 'prosemirror-model';
import { EditorState, Transaction } from 'prosemirror-state';

import { categorizeParagraphs, createReplacementNodes } from './paragraphUtils';
import { findBlockquoteRanges } from './rangeUtils';

export function unsetBlockquoteForSelection(
  state: EditorState,
  dispatch?: (tr: Transaction) => void,
  trArg?: Transaction,
): boolean {
  const { schema, selection } = state;
  const { from, to } = selection;
  const transaction = trArg || state.tr;

  const blockquotePositions = findBlockquotePositions(state.doc, from, to, schema);
  if (blockquotePositions.length === 0) {
    return false;
  }

  const { modifiedTransaction, hasModifications } = processBlockquotePositions(transaction, blockquotePositions, schema);

  if (hasModifications && dispatch) {
    dispatch(modifiedTransaction);
  }

  return hasModifications;
}

function findBlockquotePositions(doc: Node, from: number, to: number, schema: Schema): number[] {
  const positions: number[] = [];

  doc.nodesBetween(from, to, (node: Node, pos: number) => {
    if (node.type === schema.nodes.blockquote) {
      positions.push(pos);
      return false;
    }
    return true;
  });

  return positions;
}

function processBlockquotePositions(
  transaction: Transaction,
  positions: number[],
  schema: Schema,
): { modifiedTransaction: Transaction; hasModifications: boolean } {
  let currentTransaction = transaction;
  let hasModifications = false;

  // 後から前に引用の位置を探索
  for (let currentPosition = positions.length - 1; currentPosition >= 0; currentPosition--) {
    const blockquotePosition = positions[currentPosition];
    const resolvedPosition = currentTransaction.doc.resolve(blockquotePosition + 1);

    const blockquoteDepth = findBlockquoteDepth(resolvedPosition, schema);
    if (blockquoteDepth === null) {
      continue;
    }

    const blockquoteStart = resolvedPosition.before(blockquoteDepth);
    const blockquoteEnd = resolvedPosition.after(blockquoteDepth);

    if (blockquoteStart >= blockquoteEnd) {
      continue;
    }

    const blockquoteNode = currentTransaction.doc.nodeAt(blockquoteStart);
    if (blockquoteNode?.type === schema.nodes.blockquote) {
      currentTransaction = currentTransaction.replaceWith(
        blockquoteStart,
        blockquoteEnd,
        blockquoteNode.content,
      );
      hasModifications = true;
    }
  }

  return { modifiedTransaction: currentTransaction, hasModifications };
}

function findBlockquoteDepth(resolvedPosition: ResolvedPos, schema: Schema): number | null {
  for (let depth = resolvedPosition.depth; depth > 0; depth--) {
    if (resolvedPosition.node(depth).type === schema.nodes.blockquote) {
      return depth;
    }
  }
  return null;
}

export function liftBlockquoteAtSelection(
  state: EditorState,
  dispatch?: (tr: Transaction) => void,
  trArg?: Transaction,
): boolean | Transaction {
  const { schema, selection } = state;
  const { $from } = selection;
  const transaction = trArg || state.tr;

  const blockquoteDepth = findBlockquoteDepth($from, schema);
  if (blockquoteDepth === null) {
    return false;
  }

  const paragraphDepth = blockquoteDepth + 1;
  if (paragraphDepth > $from.depth) {
    return false;
  }

  const $start = transaction.doc.resolve($from.before(paragraphDepth));
  const $end = transaction.doc.resolve($from.after(paragraphDepth));
  const blockRange = $start.blockRange($end);

  if (!blockRange || blockRange.depth === 0) {
    return false;
  }

  const modifiedTransaction = transaction.lift(blockRange, blockRange.depth - 1);

  if (dispatch && !trArg) {
    dispatch(modifiedTransaction);
    return true;
  }

  return trArg ? modifiedTransaction : true;
}

/**
 * 選択された部分のみからblockquote書式を削除し、選択されていない部分は引用のまま保持する
 * これにより複数段落のblockquote内での部分的な引用解除が可能になる
 */
export function unsetBlockquoteForSelectionPartially(
  state: EditorState,
  dispatch?: (tr: Transaction) => void,
  trArg?: Transaction,
): boolean {
  const { schema, selection } = state;
  const { from, to } = selection;
  const transaction = trArg || state.tr;

  const blockquoteRanges = findBlockquoteRanges(state, from, to);
  if (blockquoteRanges.length === 0) {
    return false;
  }

  const { modifiedTransaction, hasModifications } = processBlockquoteRanges(
    transaction,
    blockquoteRanges,
    schema,
    from,
    to,
  );

  if (hasModifications && dispatch) {
    dispatch(modifiedTransaction);
  }

  return hasModifications;
}

function processBlockquoteRanges(
  transaction: Transaction,
  blockquoteRanges: Array<{ node: Node; start: number; end: number }>,
  schema: Schema,
  selectionFrom: number,
  selectionTo: number,
): { modifiedTransaction: Transaction; hasModifications: boolean } {
  let currentTransaction = transaction;
  let hasModifications = false;

  // 後から前に引用の位置を探索
  for (let currentPosition = blockquoteRanges.length - 1; currentPosition >= 0; currentPosition--) {
    const { node, start, end } = blockquoteRanges[currentPosition];
    const selectionStart = Math.max(selectionFrom, start + 1);
    const selectionEnd = Math.min(selectionTo, end - 1);

    if (selectionStart >= selectionEnd) {
      continue;
    }

    const paragraphGroups = categorizeParagraphs(node, start, selectionFrom, selectionTo);
    const replacementNodes = createReplacementNodes(schema, node, paragraphGroups);

    currentTransaction = currentTransaction.replaceWith(
      start,
      end,
      Fragment.from(replacementNodes),
    );
    hasModifications = true;
  }

  return { modifiedTransaction: currentTransaction, hasModifications };
}
